import React, { ReactNode } from 'react';
import { Form, FormProps } from 'react-final-form';
import { useHistory } from 'react-router-dom';
import { SubmissionErrors, FormApi, FORM_ERROR } from 'final-form';
import { ConditionalField, Field } from 'components/Forms';
import ErrorDisplay from 'components/ErrorDisplay';
import { InputFieldProps, FormValues } from './utils';
import Radio from './Radio';

interface ChildProps {
  submitting: boolean;
  submitSucceeded: boolean;
  submitFailed: boolean;
  submitErrors: SubmissionErrors;
  dirty: boolean;
  form: FormApiWithRestart;
  values: FormValues;
}
interface FormComponentProps extends FormProps {
  validationFunc: (values: FormValues) => SubmissionErrors | undefined;
  onSubmit: (
    fieldValues: FormValues,
    form: FormApi
  ) => Promise<SubmissionErrors | undefined | void> | void;
  children?: (props: ChildProps) => ReactNode | void;
  autoReset?: boolean;
  goBackToPreviousPage?: boolean;
  fields?: InputFieldProps[];
  initialValues?: FormValues;
  ref?: any;
  className?: string;
  validateOnBlur?: boolean;
}
// Adding this since polyfilling this missing type makes TS complain
// about everything else in the form render props 😩
export interface FormApiWithRestart extends FormApi<FormValues> {
  values: Record<string, any>;
  restart: () => void;
}

const FormComponent = ({
  validationFunc,
  onSubmit,
  children,
  fields,
  initialValues,
  goBackToPreviousPage = true,
  keepDirtyOnReinitialize,
  className
}: FormComponentProps) => {
  const history = useHistory();

  if (!children && !fields) {
    throw new Error(
      'FormComponent expects either children or a fields prop, but neither were passed.'
    );
  }
  return (
    <Form
      onSubmit={onSubmit}
      validate={validationFunc}
      validateOnBlur
      initialValues={initialValues}
      keepDirtyOnReinitialize={keepDirtyOnReinitialize}
      render={({
        submitting,
        handleSubmit,
        submitSucceeded,
        submitFailed,
        submitErrors,
        form,
        dirty,
        error,
        values
      }) => {
        if (submitSucceeded && goBackToPreviousPage) {
          // @ts-ignore
          setTimeout(() => history.goBack(), 1000);
        }
        return (
          <div>
            <form onSubmit={handleSubmit} className={className}>
              {submitErrors?.[FORM_ERROR] && (
                <ErrorDisplay
                  status={{
                    message: submitErrors[FORM_ERROR]
                  }}
                />
              )}
              {error && (
                <ErrorDisplay
                  status={{
                    message: error
                  }}
                />
              )}
              {!fields
                ? null
                : fields.map(({ conditional, ...field }) => {
                    const { inputType, options } = field;

                    if (conditional) {
                      return (
                        <ConditionalField
                          key={`${conditional.when}-${field.id}`}
                          {...conditional}
                        >
                          <Field key={field.id} field={field} />
                        </ConditionalField>
                      );
                    }

                    if (inputType === 'radio' && options) {
                      const meta = form.getFieldState(field.name);

                      return <Radio key={field.id} {...{ field, meta }} />;
                    }

                    return <Field key={field.id} field={field} />;
                  })}
              {!children
                ? null
                : children({
                    submitting,
                    submitSucceeded,
                    submitFailed,
                    submitErrors,
                    dirty,
                    form: form as FormApiWithRestart,
                    values
                  })}
            </form>
          </div>
        );
      }}
    />
  );
};

export default FormComponent;
